// src/pages/ApplicationSeason/components/SeasonTable/SeasonTable.tsx
import {
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Badge,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Season, SeasonType } from '@/model';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';

export interface SeasonTableProps {
  data: Season[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick: (item: Season) => void;
  onDeleteClick: (item: Season) => void;
}

export const SeasonTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  totalItems,
  sorting,
  onSortingChange,
  onPaginationChange,
  onEditClick,
  onDeleteClick,
}: SeasonTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<Season>();

  const getTypeLabel = (type?: SeasonType) => {
    if (!type) return '-';

    switch (type) {
      case SeasonType.LEAGUE:
        return t(
          'pages.application_season.components.seasons_table.seasons_table_type_league',
        );
      case SeasonType.KNOCKOUT:
        return t(
          'pages.application_season.components.seasons_table.seasons_table_type_knockout',
        );
      case SeasonType.HYBRID:
        return t(
          'pages.application_season.components.seasons_table.seasons_table_type_hybrid',
        );
      default:
        return type;
    }
  };

  const columns = [
    // DATA DA CRIAÇÃO
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t(
        'pages.application_season.components.seasons_table.seasons_table_created_at',
      ),
    }),

    // COMPETIÇÃO
    columnHelper.display({
      id: 'championship_name',
      header: t(
        'pages.application_season.components.seasons_table.seasons_table_championship',
      ),
      cell: props => {
        const row = props.row.original;
        const name = row.championship?.name ?? '-';
        return <Text fontWeight="medium">{name}</Text>;
      },
    }),

    // SIGLA
    columnHelper.display({
      id: 'championship_acronym',
      header: t(
        'pages.application_season.components.seasons_table.seasons_table_acronym',
      ),
      cell: props => {
        const row = props.row.original;
        const acronym = (row.championship as any)?.acronym ?? '-';
        return <Text>{acronym}</Text>;
      },
    }),

    // TEMPORADA (ANO)
    columnHelper.accessor('year', {
      cell: props => <Text>{props.getValue()}</Text>,
      header: t(
        'pages.application_season.components.seasons_table.seasons_table_year',
      ),
    }),

    // FORMATO
    columnHelper.accessor('type', {
      cell: props => <Text>{getTypeLabel(props.getValue())}</Text>,
      header: t(
        'pages.application_season.components.seasons_table.seasons_table_type',
      ),
    }),

    // EXIBIR EM TABELAS (is_filtered)
    columnHelper.accessor('is_filtered', {
      cell: props => {
        const value = props.getValue();
        return (
          <Text>
            {value
              ? t(
                  'pages.application_season.components.seasons_table.seasons_table_show_in_tables_yes',
                )
              : t(
                  'pages.application_season.components.seasons_table.seasons_table_show_in_tables_no',
                )}
          </Text>
        );
      },
      header: t(
        'pages.application_season.components.seasons_table.seasons_table_show_in_tables',
      ),
    }),

    // SITUAÇÃO (is_active)
    columnHelper.accessor('is_active', {
      cell: props => {
        const value = props.getValue();
        return (
          <Badge colorScheme={value ? 'green' : 'red'}>
            {value
              ? t(
                  'pages.application_season.components.seasons_table.seasons_table_status_active',
                )
              : t(
                  'pages.application_season.components.seasons_table.seasons_table_status_inactive',
                )}
          </Badge>
        );
      },
      header: t(
        'pages.application_season.components.seasons_table.seasons_table_status',
      ),
    }),

    // AÇÕES
    columnHelper.display({
      id: 'actions',
      header: t(
        'pages.application_season.components.seasons_table.seasons_table_actions',
      ),
      cell: props => (
        <Flex justifyContent="end">
          <Menu>
            <MenuButton
              textColor="text.table"
              as={IconButton}
              variant="ghost"
              size="sm"
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t(
                'pages.application_season.components.seasons_table.seasons_table_show_more_actions',
              )}
            />
            <MenuList textColor="white">
              <MenuItem
                onClick={() => onEditClick?.(props.row.original as Season)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name="eye" />
                  </Text>
                }
              >
                <Text>
                  {t(
                    'pages.application_season.components.seasons_table.seasons_table_view_details',
                  )}
                </Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original as Season)}
                icon={
                  <Text>
                    <ChakraFeatherIcon
                      name="trash-2"
                      color="red.500"
                      w="18px"
                    />
                  </Text>
                }
              >
                <Text>
                  {t(
                    'pages.application_season.components.seasons_table.seasons_table_delete',
                  )}
                </Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={columns}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
